import { useEffect } from "react";
import { useForm } from "@tanstack/react-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { useStores } from "@/hooks/use-stores";
import { useCreateCredential, useUpdateCredential } from "@/hooks/use-shipping";
import { COMPANY_TYPES } from "@/lib/shipping/types";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credential?: any;
};

const defaultValues = {
  storeId: "",
  name: "",
  companyType: "XE",
  apiUrl: "",
  username: "",
  password: "",
  addressNumber: "",
  phone: "",
  paymentType: 9,
  goodsType: "odjeca",
  color: "#f97316",
  active: true,
  position: 0,
};

export function CredentialDialog({ open, onOpenChange, credential }: Props) {
  const isEdit = !!credential;
  const { data: stores } = useStores();
  const createMut = useCreateCredential();
  const updateMut = useUpdateCredential();

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      try {
        if (isEdit) {
          const body: any = { ...value };
          if (!body.username) delete body.username;
          if (!body.password) delete body.password;
          await updateMut.mutateAsync({ id: credential.id, ...body });
          toast.success("Kurirski nalog ažuriran");
        } else {
          await createMut.mutateAsync(value);
          toast.success("Kurirski nalog kreiran");
        }
        onOpenChange(false);
      } catch (e: any) {
        toast.error(e.message || "Greška");
      }
    },
  });

  useEffect(() => {
    if (credential) {
      form.reset({
        storeId: credential.storeId,
        name: credential.name,
        companyType: credential.companyType,
        apiUrl: credential.apiUrl,
        username: "",
        password: "",
        addressNumber: credential.addressNumber,
        phone: credential.phone || "",
        paymentType: credential.paymentType,
        goodsType: credential.goodsType,
        color: credential.color,
        active: credential.active,
        position: credential.position,
      });
    } else {
      form.reset(defaultValues);
    }
  }, [credential, open]);

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Uredi kurirski nalog" : "Novi kurirski nalog"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); form.handleSubmit(); }} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Trgovina</Label>
            <form.Field name="storeId">
              {(field) => (
                <Select value={field.state.value} onValueChange={(v) => field.handleChange(v)}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Odaberi trgovinu" /></SelectTrigger>
                  <SelectContent>
                    {stores?.map((s: any) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </form.Field>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Naziv</Label>
            <form.Field name="name">
              {(field) => (
                <Input value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} required />
              )}
            </form.Field>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Tip kurirske službe</Label>
            <form.Field name="companyType">
              {(field) => (
                <Select value={field.state.value} onValueChange={(v) => field.handleChange(v)}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(COMPANY_TYPES).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </form.Field>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>API URL</Label>
            <form.Field name="apiUrl">
              {(field) => (
                <Input value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} required />
              )}
            </form.Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Korisničko ime</Label>
              <form.Field name="username">
                {(field) => (
                  <Input value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} required={!isEdit} placeholder={isEdit ? "Ostavi prazno za nepromijenjeno" : ""} />
                )}
              </form.Field>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Lozinka</Label>
              <form.Field name="password">
                {(field) => (
                  <Input type="password" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} required={!isEdit} placeholder={isEdit ? "Ostavi prazno za nepromijenjeno" : ""} />
                )}
              </form.Field>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Broj adrese (lokacija)</Label>
              <form.Field name="addressNumber">
                {(field) => (
                  <Input value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} />
                )}
              </form.Field>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Telefon (na naljepnici)</Label>
              <form.Field name="phone">
                {(field) => (
                  <Input value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} placeholder="Opciono — zamjenjuje telefon firme" />
                )}
              </form.Field>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Tip plaćanja</Label>
              <form.Field name="paymentType">
                {(field) => (
                  <Select value={String(field.state.value)} onValueChange={(v) => field.handleChange(Number(v))}>
                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="9">Pouzeće</SelectItem>
                      <SelectItem value="1">Virman</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </form.Field>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Vrsta robe</Label>
              <form.Field name="goodsType">
                {(field) => (
                  <Input value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} />
                )}
              </form.Field>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Boja</Label>
              <form.Field name="color">
                {(field) => (
                  <div className="flex gap-2 items-center">
                    <input type="color" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} className="w-8 h-8 border-0 p-0 cursor-pointer" />
                    <Input value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} className="flex-1" />
                  </div>
                )}
              </form.Field>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Pozicija</Label>
              <form.Field name="position">
                {(field) => (
                  <Input type="number" value={field.state.value} onChange={(e) => field.handleChange(Number(e.target.value))} />
                )}
              </form.Field>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <form.Field name="active">
                  {(field) => (
                    <Checkbox checked={field.state.value} onCheckedChange={(v) => field.handleChange(!!v)} />
                  )}
                </form.Field>
                Aktivan
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? "Sačuvaj" : "Kreiraj"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
